import { formatInTimeZone } from 'date-fns-tz';
import { useMemo, useState } from 'react';
import { AccountingAuditExport } from 'src/components/AccountingAudit/AccountingAuditExport';
import { OkButton } from 'src/components/Buttons/OkButton';
import { MarketplaceLogo } from 'src/components/common/MarketplaceLogo';
import { MarketplacePaymentStatePill } from 'src/components/common/MarketplacePaymentStatePill/MarketplacePaymentStatePill';
import { Content, useContent } from 'src/contexts/ContentContext';
import { useSiteTimezoneContext } from 'src/contexts/SiteTimezoneContext/SiteTimezoneContext';
import { DEFAULT_TIMEZONE_LOCAL } from 'src/contexts/SiteTimezoneContext/SiteTimezoneContext.constants';
import { vars } from 'src/core/themes';
import { Stack } from 'src/core/ui';
import { useAdyenPaymentDeeplink } from 'src/hooks/api/useAdyenPaymentDeeplink';
import { useUserHasAnyOfPermissions } from 'src/hooks/useUserHasAnyOfPermissions';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { usePayRetailSaleLauncher } from 'src/modals/SaleDetails/components/MarketplacePaymentTerms/usePayRetailSaleLauncher';
import { DownloadIcon, IconsFill, LockIcon } from 'src/svgs/Viagogo';
import { ContentId } from 'src/utils/constants/contentId';
import { MARKETPLACE_PAYMENT_TERM_TO_CID } from 'src/utils/constants/contentIdMaps';
import {
  Feature,
  Marketplace,
  MarketplacePaymentState,
  MarketplaceSalePayment,
  Permission,
} from 'src/WebApiController';

import * as styles from './MarketplacePaymentHeaderDetails.css';
import { MarketplacePaymentsDetailsFilter } from './MarketplacePaymentsDetailsFilter';

interface MarketplacePaymentHeaderDetailsProps {
  marketplaceSalePayment: MarketplaceSalePayment;
  /**
   * POS-9097: the single retail sale this payment covers, resolved by the parent
   * from the payment lines (null when the payment spans multiple sales / no
   * mapped sale). Enables the header-level "Add Payment" action.
   */
  retailSaleId?: number | null;
  /** Invoked after a successful header-level payment, to refresh the view. */
  onPaid?: () => void | Promise<void>;
}

export const MarketplacePaymentHeaderDetails = ({
  marketplaceSalePayment,
  retailSaleId,
  onPaid,
}: MarketplacePaymentHeaderDetailsProps) => {
  const { timeZone } = useSiteTimezoneContext();
  const lockedText = useContent(ContentId.Locked);

  const hasMarketplacePaymentLineFilterFeature = useUserHasFeature(
    Feature.MarketplacePaymentLineFilter
  );
  const hasAccountingAuditExport = useUserHasFeature(
    Feature.AccountingAuditExport
  );
  const hasAccountingViewPermission = useUserHasAnyOfPermissions(
    Permission.Accounting_ViewAll,
    Permission.Accounting_ViewPaymentsOnSale
  );
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);
  const accountingExportTitle = useContent(ContentId.ExportAccountingData);

  // POS-9098: only a real Adyen PSP reference (backfilled by the webhook) is deeplinkable — the
  // merchant-ref fallback and external payments have pmtRefId === extPmtId, so exclude those.
  const deeplinkReferenceId = useMemo(
    () =>
      marketplaceSalePayment.pmtRefId &&
      marketplaceSalePayment.pmtRefId !== marketplaceSalePayment.extPmtId
        ? marketplaceSalePayment.pmtRefId
        : null,
    [marketplaceSalePayment.extPmtId, marketplaceSalePayment.pmtRefId]
  );
  const { data: adyenDeeplinkUrl } = useAdyenPaymentDeeplink(
    deeplinkReferenceId,
    marketplaceSalePayment.mkp !== Marketplace.Retail
  );

  // POS-9097: offer "Add Payment" on an unpaid retail payment, reusing the same
  // launcher as the payment-line row action (resolves the sale on click, routing
  // grouped sales to the SalesGroup modal). Gated to a payment that maps to a
  // single retail sale — multi-sale payments are paid via the per-line action.
  const hasRetailSaleFeature = useUserHasFeature(Feature.RetailSale);
  const { triggerPay, isFetching: isRetailSaleFetching } =
    usePayRetailSaleLauncher(retailSaleId, { onPaymentSuccess: onPaid });
  const canPayRetailSale =
    hasRetailSaleFeature &&
    marketplaceSalePayment.mkp === Marketplace.Retail &&
    marketplaceSalePayment.state === MarketplacePaymentState.Unpaid &&
    retailSaleId != null;

  return (
    <Stack
      justifyContent="spaceBetween"
      width="full"
      className={styles.wrapper}
    >
      <Stack direction="column" gap="m">
        <div>
          <span className={styles.title}>
            <Content id={ContentId.PaymentId} />:
          </span>
          <span>{marketplaceSalePayment.extPmtId}</span>
        </div>
        {marketplaceSalePayment.pmtDate && (
          <div>
            <span className={styles.title}>
              <Content id={ContentId.PaymentDate} />:
            </span>
            <span>
              {formatInTimeZone(
                new Date(marketplaceSalePayment.pmtDate),
                timeZone ?? DEFAULT_TIMEZONE_LOCAL,
                'do LLL yyyy'
              )}
            </span>
          </div>
        )}
        {marketplaceSalePayment.mkpPmtTerm && (
          <div>
            <span className={styles.title}>
              <Content id={ContentId.MarketplacePaymentTerm} />:
            </span>
            <span>
              <Content
                id={
                  MARKETPLACE_PAYMENT_TERM_TO_CID[
                    marketplaceSalePayment.mkpPmtTerm
                  ]
                }
              />
            </span>
          </div>
        )}
        {adyenDeeplinkUrl && (
          <div>
            <span className={styles.title}>
              <Content id={ContentId.ReferenceId} />:
            </span>
            <span>
              <a
                href={adyenDeeplinkUrl}
                target="_blank"
                rel="noreferrer"
                className={styles.referenceLink}
              >
                {deeplinkReferenceId}
              </a>
            </span>
          </div>
        )}
      </Stack>
      <Stack direction="column" gap="m" alignItems="center">
        <Stack alignItems="center" gap="m" alignSelf="start">
          {canPayRetailSale && (
            <OkButton
              textContentId={ContentId.AddPayment}
              variant="outlineBrand"
              onClick={triggerPay}
              disabled={isRetailSaleFetching}
            />
          )}
          {hasAccountingAuditExport && hasAccountingViewPermission && (
            <>
              <DownloadIcon
                withHoverEffect
                fill={IconsFill.currentColor}
                title={accountingExportTitle}
                onClick={() => setIsExportDialogOpen(true)}
              />
              <AccountingAuditExport
                entityId={marketplaceSalePayment.id}
                isOpen={isExportDialogOpen}
                onClose={() => setIsExportDialogOpen(false)}
                streamOptions={[
                  {
                    value: 'invoice_payment',
                    label: <Content id={ContentId.ExportInvoicePayment} />,
                  },
                ]}
              />
            </>
          )}
          {marketplaceSalePayment.isLocked && (
            <LockIcon
              size={vars.iconSize.m}
              fill={vars.color.textBrand}
              withHoverEffect
              title={lockedText}
            />
          )}
          <MarketplacePaymentStatePill state={marketplaceSalePayment.state} />
          <MarketplaceLogo marketplace={marketplaceSalePayment.mkp} />
        </Stack>
        {hasMarketplacePaymentLineFilterFeature && (
          <MarketplacePaymentsDetailsFilter />
        )}
      </Stack>
    </Stack>
  );
};
